import LazyImage, { LazyImageProps } from "@eisinc/MobiusOverrides/LazyImage";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import translate from "@insite/client-framework/Translate";
import { BrandDto } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    brand: BrandDto;
    /**
     * If true, the small brand logo will be displayed.
     * If false, the brand name will be displayed instead.
     * Default value: false */
    showLogo?: boolean;
    extendedStyles?: ProductBrandStyles;
    showBrandTextLabel?: boolean;
}

type Props = OwnProps;

export interface ProductBrandStyles {
    logoImage?: LazyImageProps;
    nameLink?: LinkPresentationProps;
    nameText?: TypographyProps;
    labelTextStyle?: TypographyProps;
    printedLogo?: InjectableCss;
}

const nameText: TypographyProps = {
    css: css`
        ${wrapInContainerStyles}
    `,
    size: "14px",
    lineHeight: "18px",
    weight: "normal",
};

export const productBrandStyles: ProductBrandStyles = {
    nameText,
    nameLink: {
        css: css``,
        typographyProps: nameText,
    },
    logoImage: {
        css: css`
            @media print {
                display: none;
            }
        `,
    },
    printedLogo: {
        css: css`
            display: none;
            @media print {
                display: initial;
                max-width: 100px;
                max-height: 100px;
            }
        `,
    },
    labelTextStyle: {
        size: 14,
        lineHeight: "18px",
        weight: "bold",
    },
};

const ProductBrand: FC<Props> = ({ brand, showLogo = false, extendedStyles, showBrandTextLabel = true }) => {
    if (!brand) {
        return null;
    }

    const [styles] = React.useState(() => mergeToNew(productBrandStyles, extendedStyles));
    let printedDisplay;
    let brandDisplay;
    if (showLogo && brand.logoSmallImagePath) {
        const altText = brand.logoImageAltText || brand.name;
        brandDisplay = <LazyImage {...styles.logoImage} src={brand.logoSmallImagePath} altText={altText} />;
        printedDisplay = (
            <StyledWrapper {...styles.printedLogo}>
                <img
                    style={{ maxWidth: "100px", maxHeight: "100px", width: "auto", height: "auto" }}
                    src={brand.logoSmallImagePath}
                    alt={altText}
                />
            </StyledWrapper>
        );
    } else if (showBrandTextLabel) {
        brandDisplay = brand.detailPagePath ? (
            brand.name
        ) : (
            <Typography {...styles.nameText} data-test-selector="brandName">
                {brand.name}
            </Typography>
        );
    } else {
        brandDisplay = brand.detailPagePath ? (
            brand.name
        ) : (
            <Typography {...styles.nameText} data-test-selector="brandName">
                {brand.name}
            </Typography>
        );
    }

    return (
        <>
            {(!showLogo || !brand.logoSmallImagePath) && showBrandTextLabel && (
                <Typography {...styles.labelTextStyle} data-test-selector="brandNameLabel">
                    {translate("Brand:")}&nbsp;
                </Typography>
            )}
            {brand.detailPagePath ? (
                <Link {...styles.nameLink} href={brand.detailPagePath} data-test-selector="brandLink">
                    {brandDisplay}
                    {printedDisplay}
                </Link>
            ) : (
                <>
                    {brandDisplay}
                    {printedDisplay}
                </>
            )}
        </>
    );
};

export default ProductBrand;
